import { useBalances } from '@/entities/balances'
import { useBanksData } from '@/entities/banks'
import { useMerchantData } from '@/entities/merchant/hooks'
import { useTraderData } from '@/entities/trader'
import { VerificationBanner } from '@/features/profile/ui/VerificationBanner/VerificationBanner'
import { Sidebar } from '@/features/sidebar'
import { BaseButton, SupportContact } from '@/shared/components'
import MainIcon from '@/shared/components/icons/MainIcon'
import { useActiveUser, useToast } from '@/shared/hooks'
import { ErrorBoundary } from '@sentry/react'
import clsx from 'clsx'
import { Menu } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { TraderWithdrawFilterStatus, useWithdrawsTrader } from '@/features/withdraws'
import { playMelodicNotification, unlockAudio } from '@/shared/assets/notifications/playMelodicNotification'

export const DashboardLayout = () => {
  const location = useLocation()
  useTraderData()
  useMerchantData()
  useBanksData()
  useBalances()
  const { addToast } = useToast()
  const { isLoading, isVerified } = useActiveUser()

  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  useEffect(() => {
    const applyScrollLock = () => {
      if (isSidebarOpen) {
        document.body.classList.add('overflow-hidden')
        document.body.style.position = 'fixed'
        document.body.style.width = '100%'
      } else {
        document.body.classList.remove('overflow-hidden')
        document.body.style.position = ''
        document.body.style.width = ''
      }
    }

    // slight delay helps on Safari
    const timeout = setTimeout(applyScrollLock, 10)

    return () => {
      clearTimeout(timeout)
      document.body.classList.remove('overflow-hidden')
      document.body.style.position = ''
      document.body.style.width = ''
    }
  }, [isSidebarOpen])

  useEffect(() => {
    // разблокировка звука после первого клика пользователя
    const handleClick = () => {
      unlockAudio()
      window.removeEventListener('click', handleClick)
    }
    window.addEventListener('click', handleClick, { once: true })
  }, [])

  const { data } = useWithdrawsTrader({
    status: TraderWithdrawFilterStatus.Waiting,
  })

  useEffect(() => {
    if (isLoading || !data.withdrawsData?.items?.length) return

    addToast('Новые исходящие заявки')
    playMelodicNotification()
  }, [JSON.stringify(data.withdrawsData?.items), isLoading])

  return (
    <div className={clsx('flex flex-col mx-auto px-4 w-full max-w-[1976px]', 'lg:pr-10')}>
      <div className={clsx('relative flex flex-col mt-6 w-full', 'lg:flex-row')}>
        <aside className="lg:mr-10">
          <header className="flex justify-between items-center pb-4">
            <div className="lg:pl-4">
              <MainIcon />
            </div>

            <BaseButton
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setIsSidebarOpen(true)}
            >
              <Menu className="w-6 h-6 text-text-secondary" />
            </BaseButton>
          </header>
          <div
            className={clsx(
              'left-0 z-50 fixed inset-y-0 w-screen md:w-72 transition-transform duration-300 transform',
              'lg:relative lg:translate-x-0 lg:w-auto',
              isSidebarOpen ? 'translate-x-0 ' : '-translate-x-full'
            )}
          >
            <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />
          </div>
          {isSidebarOpen && (
            <div
              className="lg:hidden z-40 fixed inset-0 bg-dark-bg/50 dark:bg-dark-bg/80"
              onClick={() => setIsSidebarOpen(false)}
            ></div>
          )}
        </aside>

        <div className="flex flex-col flex-1 min-w-0 min-h-[calc(100vh-30px)]">
          {!isVerified && !isLoading && (
            <div className="flex justify-between items-center mb-10 lg:mb-8">
              <VerificationBanner />
            </div>
          )}
          <main className="flex-grow p-0.5 w-full min-w-0 overflow-hidden">
            <ErrorBoundary
              key={location.pathname}
              fallback={({ error }) => {
                const message = error instanceof Error ? error.message : 'Неизвестная ошибка'
                return (
                  <div className="flex flex-col justify-center items-center gap-3 mt-[20%]">
                    <h2 className="text-xl">Что-то пошло не так. Попробуйте обновить страницу.</h2>
                    <p className="text-text-secondary text-sm">Ошибка: {message}</p>
                  </div>
                )
              }}
            >
              <Outlet />
            </ErrorBoundary>
          </main>
          <footer className="py-6">
            <SupportContact />
          </footer>
        </div>
      </div>
    </div>
  )
}
